import clsx from "clsx";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import IcnChevronRight from "@assets/icons/chevron-right-sharp.svg";
import IcnTag from "@assets/icons/tag.svg";
import { formatMessage } from "@app/translations/intl";
import { getOptimizedImageUrl } from "@app/util/image-optimization";
import { Icon } from "@app/components/icon/icon";
import { RelAttributes } from "@app/constants";
import { DealLike } from "@app/components/deal-cards/deal-like/deal-like";
import { ImageComponent } from "@app/core/image/image.component";
import { ClickableComponent } from "@app/core/clickable";
import { FeaturedFilterLabel } from "@app/core/featured-filter-label/featured-filter-label";
import { findPlatformContent } from "@app/api/wp-page-fetcher/utils/platform-content-array";
import { PRODUCT_COMPARISON_LINK_PLATFORMS } from "@app/constants/platform";

import { getShowBeforePrice } from "../deal-card-wrapper/deal-card-wrapper";
import { IOutFeaturedDealDTO, SHOW_DISCOUNT_GTI } from "../new-deal-card";
import { getAffiliateDealLinks } from "../deal-card-landscape/deal-card-landscape";
import styles from "./deal-card-no-price.module.scss";

export type DealCardNoPriceProps = {
  deal: IOutFeaturedDealDTO;
  responsive?: boolean;
  isSponsored?: boolean;
  platformId: number;
  variant?: "normal" | "mobile-search";
};

export const DealCardNoPrice = (props: DealCardNoPriceProps) => {
  const { id, discount, image, merchant, name, price, beforePrice, featuredFilter } = props.deal;
  const { enableDealAlerts } = findPlatformContent(props.platformId);

  const showBeforePrice = getShowBeforePrice(beforePrice, price);
  const showDiscount = showBeforePrice && (discount || 0) >= SHOW_DISCOUNT_GTI;

  const cardBorderStyles = clsx(props.variant === "mobile-search" && styles.mobileSearch);

  const { outMerchantLink, outDealLink } = getAffiliateDealLinks(id, merchant?.slug);

  const comparisonProduct = props.deal.product;
  const dealCount = comparisonProduct?.dealCount ?? 0;
  const showComparisonLink =
    !!comparisonProduct?.slug && dealCount >= 1 && PRODUCT_COMPARISON_LINK_PLATFORMS.includes(props.platformId);
  const productUrl = `${formatMessage({ id: "global.productOverviewLink" })}${comparisonProduct?.slug}/`;

  const handleComparisonLinkClick = () => {
    if (window && window.dataLayer) {
      window.dataLayer.push({
        event: "productComparisonLinkClick",
        dealId: props.deal.id,
        productSlug: comparisonProduct!.slug
      });
    }
  };

  return (
    <div className={clsx(styles.dealCard, cardBorderStyles, props.responsive && styles.responsive)}>
      {enableDealAlerts && <DealLike deal={props.deal} />}
      <a rel="sponsored" target="_blank" href={outDealLink} className={styles.leftSection}>
        {featuredFilter?.name && (
          <FeaturedFilterLabel
            className={styles.featuredFilter}
            label={featuredFilter.label}
            text={featuredFilter.name}
            corners="diagonal-left"
          />
        )}
        {props.isSponsored && (
          <div className={styles.sponsored}>{formatMessage({ id: "dealCard.sponsoredLabel" })}</div>
        )}
        <div className={styles.image}>
          <ImageComponent
            src={getOptimizedImageUrl({
              url: image?.url,
              height: 120
            })}
            alt={image?.alt}
            title={image?.title}
            isBlocking
          />

          {showDiscount && (
            <div className={styles.discountLabel} style={{ width: !beforePrice && !price ? "100%" : "auto" }}>
              <span className={styles.discount}>{`-${discount}%`}</span>
            </div>
          )}
        </div>
      </a>
      <div className={styles.rightSection}>
        <a rel="sponsored" target="_blank" href={outDealLink} className={styles.rightSectionLink}>
          <div className={styles.content}>
            <div className={styles.subtitleWrapper}>
              {merchant?.name && (
                <span title={merchant.name} className={styles.merchantName}>
                  {merchant.name}
                </span>
              )}
            </div>

            <div className={styles.titleWrapper}>
              <span className={styles.title}>
                <h4 title={name}>{name}</h4>
              </span>
            </div>
          </div>

          <div className={styles.action}>
            <ClickableComponent
              title={formatMessage({ id: "dealCard.button.toCampaignPageOut" })}
              variant="primary-2025"
              iconStyle="filled"
              iconRight={IcnChevronRight}
              iconSize="12px"
              fullWidth
              isNonClickable
              relAttribute={RelAttributes.SPONSORED}
            />
          </div>
        </a>
        {showComparisonLink ? (
          <Link
            className={clsx(styles.outMerchantLink, styles.comparisonLink)}
            to={productUrl}
            onClick={handleComparisonLinkClick}
          >
            <Icon icon={IcnTag} fillColor="#999999" size="9px" />
            <span className={styles.merchantLinkText}>
              <FormattedMessage
                id={dealCount >= 2 ? "dealCard.compareProductPrices" : "dealCard.viewProductPrice"}
                values={{
                  dealCount,
                  accent: (chunks: React.ReactNode) => <span className={styles.comparisonAccent}>{chunks}</span>
                }}
              />
            </span>
          </Link>
        ) : (
          merchant?.slug && (
            <a className={styles.outMerchantLink} target="_blank" rel="sponsored" href={outMerchantLink}>
              <span className={styles.merchantLinkText}>{formatMessage({ id: "dealCard.showAllMerchantDeals" })}</span>
              <Icon icon={IcnChevronRight} fillColor="#999999" size="9px" />
            </a>
          )
        )}
      </div>
    </div>
  );
};
